import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { format, differenceInMinutes } from 'date-fns';
import { CheckCircle, XCircle, RotateCcw, Edit, MessageSquare, AlertCircle, AlertTriangle, Info, BadgeCheck, RotateCw } from 'lucide-react';
import { generateInitials, generateDisplayName } from '@/utils/avatarUtils';
import { CommentInfoIcon } from '@/components/timesheets/CommentInfoIcon';
import type { PayrollVerificationStatus } from '@/services/timesheets/types';

interface TimeEntry {
  id: string;
  clock_in: string;
  clock_out: string | null;
  status: 'pending' | 'approved' | 'rejected';
  approved_at: string | null;
  approved_by_id: string | null;
  entry_type?: 'automatic' | 'manual';
  profile: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
    avatar_url?: string | null;
  };
  has_comments?: boolean;
  payroll_status?: PayrollVerificationStatus;
  attendance_flags?: Array<{
    id: string;
    flag_type: string;
    flag_severity: string;
    flag_reason: string | null;
  }>;
}

interface ManagerTimesheetCardProps {
  entry: TimeEntry;
  onApproval: (entryId: string, status: 'approved' | 'rejected') => void;
  onRevert: (entryId: string) => void;
  onEdit: (entry: TimeEntry) => void;
  calculateDuration: (clockIn: string, clockOut: string | null) => string;
  getStatusBadgeVariant: (status: string) => any;
  canRevert?: boolean;
  isApprovalPending?: boolean;
  isRevertPending?: boolean;
}

export function ManagerTimesheetCard({
  entry,
  onApproval,
  onRevert,
  onEdit,
  calculateDuration,
  getStatusBadgeVariant,
  canRevert = false,
  isApprovalPending = false,
  isRevertPending = false,
}: ManagerTimesheetCardProps) {
  const getFlagBadgeVariant = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'destructive';
      case 'warning':
        return 'secondary';
      case 'info':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getFlagIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="h-3 w-3" />;
      case 'warning':
        return <AlertTriangle className="h-3 w-3" />;
      case 'info':
        return <Info className="h-3 w-3" />;
      default:
        return <AlertTriangle className="h-3 w-3" />;
    }
  };

  const getFlagTypeLabel = (flagType: string) => {
    return flagType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Employee Info */}
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage 
                src={entry.profile?.avatar_url || undefined} 
                alt={generateDisplayName(entry.profile?.first_name, entry.profile?.last_name)}
              />
              <AvatarFallback>
                {generateInitials(entry.profile?.first_name, entry.profile?.last_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold">
                  {entry.profile?.first_name || 'Unknown'} {entry.profile?.last_name || 'User'}
                </p>
                <CommentInfoIcon 
                  entryId={entry.id} 
                  hasComments={entry.has_comments || false}
                />
                {entry.entry_type === 'manual' && (
                  <Badge variant="outline" className="text-xs">
                    <Edit className="h-3 w-3 mr-1" />
                    Manual
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{entry.profile?.email || 'No email'}</p>
            </div>
          </div>

          {/* Date */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {format(new Date(entry.clock_in), 'MMM d, yyyy')}
            </span>
          </div>

          {/* Times */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Clock In</span>
              <p className="font-medium">
                {format(new Date(entry.clock_in), 'h:mm a')}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Clock Out</span>
              <p className="font-medium">
                {entry.clock_out ? format(new Date(entry.clock_out), 'h:mm a') : '-'}
              </p>
            </div>
          </div>

          {/* Duration & Status */}
          <div className="text-sm">
            <span className="text-muted-foreground">Total Hours: </span>
            <span className="font-medium">
              {calculateDuration(entry.clock_in, entry.clock_out)}
            </span>
          </div>

{/* Status & Flags & Payroll Status */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={getStatusBadgeVariant(entry.status)}>
              {entry.status}
            </Badge>
            {entry.payroll_status === 'verified' && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                <BadgeCheck className="h-3 w-3 mr-1" />
                Payroll Verified
              </Badge>
            )}
            {entry.payroll_status === 'rolled_back' && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">
                <RotateCw className="h-3 w-3 mr-1" />
                Rolled Back
              </Badge>
            )}
            {entry.attendance_flags && entry.attendance_flags.length > 0 && (
              <TooltipProvider>
                {entry.attendance_flags.map((flag) => (
                  <Tooltip key={flag.id}>
                    <TooltipTrigger asChild>
                      <Badge 
                        variant={getFlagBadgeVariant(flag.flag_severity)}
                        className="gap-1 cursor-help"
                      >
                        {getFlagIcon(flag.flag_severity)}
                        {getFlagTypeLabel(flag.flag_type)}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">{flag.flag_reason || 'Attendance discrepancy detected'}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </TooltipProvider>
            )}
          </div>

          {/* Actions */}
          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 flex-wrap">
              {entry.status === 'pending' && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onApproval(entry.id, 'approved')}
                    disabled={isApprovalPending}
                    className="flex-1 min-w-0"
                  >
                    <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onApproval(entry.id, 'rejected')}
                    disabled={isApprovalPending}
                    className="flex-1 min-w-0"
                  >
                    <XCircle className="h-4 w-4 text-red-600 mr-1" />
                    Reject
                  </Button>
                </>
              )}
              {(entry.status === 'approved' || entry.status === 'rejected') && canRevert && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRevert(entry.id)}
                  disabled={isRevertPending}
                  className="flex-1 min-w-0"
                >
                  <RotateCcw className="h-4 w-4 text-orange-600 mr-1" />
                  Revert
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(entry)}
                className={entry.status === 'pending' || ((entry.status === 'approved' || entry.status === 'rejected') && canRevert) ? 'w-full mt-2' : 'flex-1'}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
